import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchCoins, fetchCoinById, createCoin, updateCoin, deleteCoin } from '../api';

/**
 * Hook for fetching all coins
 * @param status Optional status filter
 * @returns Query result with coins data
 */
export const useCoins = (status?: string) => {
  return useQuery({
    queryKey: ['coins', status],
    queryFn: () => fetchCoins(status)
  });
};

/**
 * Hook for fetching a coin by ID
 * @param id Coin ID
 * @returns Query result with coin data
 */
export const useCoin = (id: string) => {
  return useQuery({
    queryKey: ['coin', id],
    queryFn: () => fetchCoinById(id),
    enabled: !!id // Only run the query if id is provided
  });
};

/**
 * Hook for creating a new coin
 * @returns Mutation for creating a coin
 */
export const useCreateCoin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (coinData: any) => createCoin(coinData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coins'] });
    }
  });
};

/**
 * Hook for updating a coin
 * @returns Mutation for updating a coin
 */
export const useUpdateCoin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, coinData }: { id: string, coinData: any }) => updateCoin(id, coinData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['coins'] });
      queryClient.invalidateQueries({ queryKey: ['coin', variables.id] });
    }
  });
};

/**
 * Hook for deleting a coin
 * @returns Mutation for deleting a coin
 */
export const useDeleteCoin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => deleteCoin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coins'] });
    }
  });
};
